// Guard Lineups (LU1, Trevor 2026-10-20): every on/off combination of a team's listed guards, from the report blob's
// `guard_lineups` (backend app/api/team_report/guard_lineups.py). Layout from the approved mockup: headshots (on in
// colour, off faded), then per group Poss (Off, Def) | Actual (ORtg, DRtg, Net, TOV%) | Expected (ORtg, DRtg, Net).
// Rows run by the first group's possessions; combinations with none there drop to condensed rows below.
import { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import PercentileChip from "../PercentileChip";
import { formatSigned } from "../../util/formatSigned";
import { CONDENSED_FONT_FAMILY, DISPLAY_FONT_FAMILY } from "../constants";
import type { GuardLineupGroupCells, GuardLineupMetric, GuardLineups } from "../types";

/** Possessions each way under which a group's row is a small sample: grey values, no chips (Trevor 2026-10-20). */
export const GUARD_LINEUP_FLOORS: Record<string, number> = { game: 10, last10: 25, season: 50 };

const INK = "#111";
const MUTE = "#8C8C8C";
const RULE = "#D9D9D9";
const headshot = (nbaId: string) => `https://cdn.nba.com/headshots/nba/latest/260x190/${nbaId}.png`;

type MetricKey = Exclude<keyof GuardLineupGroupCells, "oPoss" | "dPoss">;
const ACTUAL: { key: MetricKey; label: string; signed?: boolean }[] = [
  { key: "ortg", label: "ORtg" },
  { key: "drtg", label: "DRtg" },
  { key: "net", label: "Net", signed: true },
  { key: "tov", label: "TOV%" },
];
const EXPECTED: { key: MetricKey; label: string; signed?: boolean }[] = [
  { key: "xortg", label: "ORtg" },
  { key: "xdrtg", label: "DRtg" },
  { key: "xnet", label: "Net", signed: true },
];

const volume = (cells: GuardLineupGroupCells | null | undefined) => (cells ? cells.oPoss + cells.dPoss : 0);

const cellSx = { px: "4px", py: "2px", textAlign: "right" as const, whiteSpace: "nowrap" as const };
const headSx = {
  ...cellSx,
  fontFamily: CONDENSED_FONT_FAMILY,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  color: "#5E5E5E",
};
const groupStart = { borderLeft: `3px solid ${INK}` };
const subStart = { borderLeft: `1px solid ${RULE}` };

// A one-guard row ranks against league pairs (backend guard_lineups.ONE_GUARD_COHORT)
const cohortLabel = (k: number) => (k === 1 ? "league two-man lineups" : "league lineups of this size");

function Metric({ m, small, signed, k }: { m?: GuardLineupMetric; small: boolean; signed?: boolean; k: number }) {
  if (!m || m.value == null) return <span style={{ color: MUTE }}>—</span>;
  const text = signed ? formatSigned(m.value, 1) : m.value.toFixed(1);
  if (small || m.percentile == null) return <span style={{ color: small ? MUTE : INK }}>{text}</span>;
  // No numeral: 18 chips a row would not fit the page; the colour carries it and the hover names it
  return (
    <PercentileChip className="gl-chip" value={text} percentile={m.percentile} minWidth={38}
                    title={`${m.percentile}th percentile vs ${cohortLabel(k)}`} />
  );
}

function GroupCells({ cells, groupKey, condensed, showExpected, k }: { cells?: GuardLineupGroupCells | null;
                      groupKey: string; condensed?: boolean; showExpected: boolean; k: number }) {
  const COLS_PER_GROUP = 2 + ACTUAL.length + (showExpected ? EXPECTED.length : 0);
  if (condensed) {
    return <Box component="td" colSpan={COLS_PER_GROUP} sx={{ ...cellSx, ...groupStart, textAlign: "center", color: MUTE }}>—</Box>;
  }
  if (!cells) {
    return (
      <Box component="td" colSpan={COLS_PER_GROUP} sx={{ ...cellSx, ...groupStart, textAlign: "center", color: MUTE }}>
        did not play
      </Box>
    );
  }
  const floor = GUARD_LINEUP_FLOORS[groupKey] ?? 0;
  const small = cells.oPoss < floor || cells.dPoss < floor;
  const ink = small ? MUTE : INK;
  return (
    <>
      <Box component="td" sx={{ ...cellSx, ...groupStart, color: ink }}>{cells.oPoss}</Box>
      <Box component="td" sx={{ ...cellSx, color: ink }}>{cells.dPoss}</Box>
      {ACTUAL.map((c, i) => (
        <Box component="td" key={c.key} sx={{ ...cellSx, ...(i === 0 ? subStart : {}) }}>
          <Metric m={cells[c.key]} small={small} signed={c.signed} k={k} />
        </Box>
      ))}
      {showExpected && EXPECTED.map((c, i) => (
        <Box component="td" key={c.key} sx={{ ...cellSx, ...(i === 0 ? subStart : {}) }}>
          <Metric m={cells[c.key]} small={small} signed={c.signed} k={k} />
        </Box>
      ))}
    </>
  );
}

function Headshot({ nbaId, label, name, on, condensed }: { nbaId: string; label: string; name: string; on: boolean;
                    condensed: boolean }) {
  const [failed, setFailed] = useState(false);
  const w = condensed ? 30 : 44;
  const h = condensed ? 22 : 32;
  const fade = { filter: on ? "none" : "grayscale(1)", opacity: on ? 1 : 0.16 };
  if (failed) {
    return (
      <Box title={name} sx={{ width: w, height: h, mx: "auto", display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: "0.65rem", fontWeight: 700, background: on ? "#E6E6E6" : "transparent", ...fade }}>
        {label}
      </Box>
    );
  }
  return (
    <Box component="img" src={headshot(nbaId)} alt={`${name} ${on ? "on" : "off"}`} title={name}
         onError={() => setFailed(true)}
         sx={{ width: w, height: h, objectFit: "cover", objectPosition: "top", display: "block", mx: "auto", ...fade }} />
  );
}

export default function GuardLineupsSection({ data, contextNote, showTitle = true }: {
  data: GuardLineups;
  contextNote?: string;
  /** false under a page masthead that already names it (the live KPI sheet) */
  showTitle?: boolean;
}) {
  const { players, groups, rows } = data;
  // The live page has no expected points (no live shot predictions): its blob says expected: false
  const showExpected = data.expected !== false;
  const COLS_PER_GROUP = 2 + ACTUAL.length + (showExpected ? EXPECTED.length : 0);
  const first = groups[0]?.key;
  const played = rows.filter((r) => volume(r.cells[first]) > 0).sort((a, b) => volume(b.cells[first]) - volume(a.cells[first]));
  const second = groups[1]?.key;
  const rest = rows
    .filter((r) => volume(r.cells[first]) === 0)
    .sort((a, b) => (second ? volume(b.cells[second]) - volume(a.cells[second]) : 0));
  const restLabel = first === "game" ? "Not on the floor in this game" : "Not on the floor this season";
  const faces = (on: boolean[], condensed: boolean) =>
    players.map((p, i) => (
      <Box component="td" key={p.nbaId} sx={{ px: "3px", py: condensed ? 0 : "2px", width: 46, textAlign: "center" }}>
        <Headshot nbaId={p.nbaId} label={p.short} name={p.name} on={on[i]} condensed={condensed} />
      </Box>
    ));
  return (
    <Stack sx={{ px: showTitle ? 2 : 0 }} spacing={1}>
      {showTitle && (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          variant="h4"
          sx={{ fontFamily: `${DISPLAY_FONT_FAMILY} !important`, fontWeight: 800, textTransform: "uppercase",
                letterSpacing: 0, whiteSpace: "nowrap" }}
        >
          Guard Lineups
        </Typography>
        <Box sx={{ flex: 1, height: "3px", backgroundColor: INK, ml: 1.5 }} />
        {contextNote && (
          <Typography component="span" sx={{ fontFamily: CONDENSED_FONT_FAMILY, fontSize: "0.862rem", fontWeight: 700,
                                             letterSpacing: "0.06em", color: "text.secondary", textTransform: "uppercase",
                                             whiteSpace: "nowrap" }}>
            {contextNote}
          </Typography>
        )}
      </Stack>
      )}
      <Box sx={{ overflowX: "auto" }}>
        <Box
          component="table"
          data-testid="guard-lineups"
          sx={{ borderCollapse: "collapse", width: "100%", fontFamily: CONDENSED_FONT_FAMILY, fontSize: "0.95rem",
                fontVariantNumeric: "tabular-nums", "& tbody td": { borderBottom: `1px solid ${RULE}` } }}
        >
          <thead>
            <tr>
              <Box component="th" colSpan={players.length}
                   sx={{ ...headSx, textAlign: "center", color: INK, fontFamily: DISPLAY_FONT_FAMILY, fontSize: "1.05rem",
                         borderBottom: `2px solid ${INK}` }}>
                Guards on the floor
              </Box>
              {groups.map((g) => (
                <Box component="th" key={g.key} colSpan={COLS_PER_GROUP}
                     sx={{ ...headSx, ...groupStart, textAlign: "center", color: INK, fontFamily: DISPLAY_FONT_FAMILY,
                           fontSize: "1.05rem", borderBottom: `2px solid ${INK}` }}>
                  {g.label}
                </Box>
              ))}
            </tr>
            <tr>
              {players.map((p) => (
                <Box component="th" key={p.nbaId} rowSpan={2}
                     sx={{ ...headSx, textAlign: "center", color: INK, verticalAlign: "bottom",
                           borderBottom: `3px solid ${INK}` }}>
                  {p.short}
                </Box>
              ))}
              {groups.map((g) => [
                <Box component="th" key={`${g.key}-poss`} colSpan={2} sx={{ ...headSx, ...groupStart, textAlign: "center", color: INK }}>Poss</Box>,
                <Box component="th" key={`${g.key}-actual`} colSpan={ACTUAL.length} sx={{ ...headSx, ...subStart, textAlign: "center", color: INK }}>Actual</Box>,
                ...(showExpected
                  ? [<Box component="th" key={`${g.key}-expected`} colSpan={EXPECTED.length} sx={{ ...headSx, ...subStart, textAlign: "center", color: INK }}>Expected</Box>]
                  : []),
              ])}
            </tr>
            <tr>
              {groups.map((g) => [
                <Box component="th" key={`${g.key}-o`} sx={{ ...headSx, ...groupStart, borderBottom: `3px solid ${INK}` }}>Off</Box>,
                <Box component="th" key={`${g.key}-d`} sx={{ ...headSx, borderBottom: `3px solid ${INK}` }}>Def</Box>,
                ...ACTUAL.map((c, i) => (
                  <Box component="th" key={`${g.key}-a-${c.key}`} sx={{ ...headSx, ...(i === 0 ? subStart : {}), borderBottom: `3px solid ${INK}` }}>{c.label}</Box>
                )),
                ...(showExpected ? EXPECTED : []).map((c, i) => (
                  <Box component="th" key={`${g.key}-x-${c.key}`} sx={{ ...headSx, ...(i === 0 ? subStart : {}), borderBottom: `3px solid ${INK}` }}>{c.label}</Box>
                )),
              ])}
            </tr>
          </thead>
          <tbody>
            {played.map((r, i) => (
              <Box component="tr" key={r.on.join("")} data-combo={r.on.map(Number).join("")}
                   sx={{ background: i % 2 ? "#F6F6F6" : "transparent" }}>
                {faces(r.on, false)}
                {groups.map((g) => (
                  <GroupCells key={g.key} cells={r.cells[g.key]} groupKey={g.key} showExpected={showExpected} k={r.k} />
                ))}
              </Box>
            ))}
            {rest.length > 0 && (
              <tr>
                <Box component="td" colSpan={players.length + groups.length * COLS_PER_GROUP}
                     sx={{ ...headSx, textAlign: "left", pt: "14px", pb: "4px", borderBottom: `2px solid ${INK} !important` }}>
                  {restLabel}
                </Box>
              </tr>
            )}
            {rest.map((r) => (
              <Box component="tr" key={r.on.join("")} data-combo={r.on.map(Number).join("")} data-condensed="1"
                   sx={{ fontSize: "0.82rem", "& td": { py: 0 } }}>
                {faces(r.on, true)}
                {groups.map((g, gi) => (
                  <GroupCells key={g.key} cells={r.cells[g.key]} groupKey={g.key} condensed={gi === 0} k={r.k}
                              showExpected={showExpected} />
                ))}
              </Box>
            ))}
          </tbody>
        </Box>
      </Box>
      <Typography sx={{ fontFamily: CONDENSED_FONT_FAMILY, fontSize: "0.8rem", color: "text.secondary", maxWidth: "90ch" }}>
        Ratings per 100 possessions{showExpected ? "; expected from each chance's shot and free throws" : ""}. A lineup gets a possession when it
        had a scoring chance in it; free throws count for the lineup that drew the foul. Percentiles rank each row against
        league lineups of its size, a single guard's against two-man lineups. Grey: under {GUARD_LINEUP_FLOORS[first] ?? 0} possessions each way
        {second ? ` (${GUARD_LINEUP_FLOORS[second] ?? 0} for ${groups[1].label.toLowerCase()})` : ""}. Garbage time
        excluded.
      </Typography>
    </Stack>
  );
}
